import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Button,
  Card,
  Center,
  CloseButton,
  Code,
  Group,
  Loader,
  Menu,
  Pagination,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconDotsVertical,
  IconEye,
  IconForklift,
  IconPackageExport,
  IconPlus,
  IconSearch,
  IconTrash,
  IconWashDrycleanOff,
} from '@tabler/icons-react';
import { PageHeader } from '../../shared/components/PageHeader';
import { ResponsiveTable } from '../../shared/components/ResponsiveTable';
import type { DataColumn } from '../../shared/components/ResponsiveTable';
import { ApiError } from '../../shared/api/problem';
import { useCurrentWarehouseId } from '../warehouses/currentWarehouse';
import {
  listLicensePlates,
  setLicensePlateStatus,
  type LicensePlateDto,
  type LicensePlateStatus,
} from './api';
import { usePermissions } from '../auth/usePermissions';
import { LicensePlateStatusBadge } from './LicensePlateStatusBadge';
import { MovePlateModal, type PlateAction } from './MovePlateModal';
import { LicensePlateDetailModal } from './LicensePlateDetailModal';
import { CreateLicensePlatesModal } from './CreateLicensePlatesModal';

const ANY_STATUS = 'any';
const PAGE_SIZE = 50;

/**
 * License plate list and inquiry (plan M3.5).
 *
 * Paged on the server, unlike the locations and items screens. A busy warehouse accumulates a
 * plate per pallet indefinitely — they are never deleted, only retired — so the set grows without
 * bound and cannot be fetched whole. That is also why search goes to the server rather than
 * filtering a local slice.
 */
export function LicensePlatesRoute() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, { open: openCreate, close: closeCreate }] = useDisclosure(false);
  const [viewing, setViewing] = useState<LicensePlateDto | null>(null);
  const [moving, setMoving] = useState<{ plate: LicensePlateDto; action: PlateAction } | null>(null);
  const [status, setStatus] = useState<string>(ANY_STATUS);
  // From the header, not a filter here. An "all warehouses" plate list would mix one 3PL
  // client's pallets with another's, and the API refuses a warehouse other than the session's.
  const warehouseId = useCurrentWarehouseId();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [actionError, setActionError] = useState<string | null>(null);

  const permissions = usePermissions();
  // Moving a pallet of stock is a different authority from printing a label, so the two actions on
  // this screen are gated separately.
  const canMove = permissions.has('inventory:move');

  const effectiveStatus = status === ANY_STATUS ? undefined : (status as LicensePlateStatus);

  const query = useQuery({
    queryKey: [
      'license-plates',
      effectiveStatus ?? 'any',
      warehouseId,
      search.trim(),
      page,
    ] as const,
    queryFn: ({ signal }) =>
      listLicensePlates(
        {
          status: effectiveStatus,
          warehouseId,
          search: search.trim() || undefined,
          skip: (page - 1) * PAGE_SIZE,
          take: PAGE_SIZE,
        },
        signal,
      ),
    // Keeps the table on screen while a filter change loads, instead of flashing the spinner.
    placeholderData: (previous) => previous,
  });

  // Any filter change invalidates the current page number: page 4 of an unfiltered list is
  // usually past the end of a filtered one, which would show an empty table that looks like
  // "no plates" rather than "wrong page".
  useEffect(() => {
    setPage(1);
  }, [status, warehouseId, search]);

  const rows = query.data?.items ?? [];
  const total = query.data?.totalCount ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const retire = useMutation({
    mutationFn: ({ id, next }: { id: string; next: LicensePlateStatus }) =>
      setLicensePlateStatus(id, next),
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['license-plates'] });
    },
    onError: (e) => {
      // The server's message names the locations still holding stock, which is the actionable
      // part — so it is shown as-is rather than replaced with a generic failure.
      setActionError(
        e instanceof ApiError ? (e.problem.detail ?? e.problem.title ?? e.message) : String(e),
      );
    },
  });

  function onCreated() {
    void queryClient.invalidateQueries({ queryKey: ['license-plates'] });
  }

  /**
   * Where a plate is standing is the reason anybody opens this screen, so it is the subtitle rather
   * than a labelled row. Empty and split are still spelled out differently: both mean "no single
   * location", and showing a dash for either loses the distinction that matters — an empty plate is
   * fine, a split one is a problem.
   */
  const columns: DataColumn<LicensePlateDto>[] = useMemo(
    () => [
      {
        key: 'number',
        label: t('licensePlates.number'),
        card: 'title',
        render: (p) => <Code>{p.licensePlateNumber}</Code>,
      },
      {
        key: 'location',
        label: t('licensePlates.location'),
        card: 'subtitle',
        render: (p) => (
          <>
            {p.locationCount === 0 && (
              <Text size="sm" c="dimmed">
                {t('licensePlates.nowhere')}
              </Text>
            )}
            {p.locationCount === 1 && (
              <Group gap={6} wrap="nowrap">
                <Text size="sm">{p.locationCode}</Text>
                <Text size="xs" c="dimmed">
                  {p.warehouseCode}
                </Text>
              </Group>
            )}
            {p.locationCount > 1 && (
              <Text size="sm" c="orange.7">
                {t('licensePlates.severalPlaces', { places: p.locationCount })}
              </Text>
            )}
          </>
        ),
      },
      {
        key: 'status',
        label: t('licensePlates.status.label'),
        card: 'aside',
        render: (p) => <LicensePlateStatusBadge status={p.status} />,
      },
      {
        key: 'items',
        label: t('licensePlates.itemsOnPlate'),
        align: 'right',
        render: (p) => p.itemCount,
      },
      {
        key: 'created',
        label: t('licensePlates.created'),
        render: (p) => (
          <Text size="sm" c="dimmed">
            {new Date(p.createdAtUtc).toLocaleDateString()}
          </Text>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <PageHeader
        title={t('nav.licensePlates')}
        actions={
          <Button color="invetrak" leftSection={<IconPlus size={16} />} onClick={openCreate}>
            {t('licensePlates.new')}
          </Button>
        }
      />

      {actionError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          withCloseButton
          onClose={() => setActionError(null)}
          title={t('licensePlates.errors.retireFailed')}
        >
          {actionError}
        </Alert>
      )}

      {query.isLoading && (
        <Center py="xl">
          <Loader color="invetrak" />
        </Center>
      )}

      {query.isError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          title={t('licensePlates.errors.loadFailed')}
        >
          <Stack gap="xs">
            <Text size="sm">{(query.error as Error).message}</Text>
            {query.error instanceof ApiError && query.error.problem.correlationId && (
              <Text size="xs" c="dimmed">
                {t('errors.genericBody')} <Code>{query.error.problem.correlationId}</Code>
              </Text>
            )}
            <Group>
              <Button size="xs" variant="default" onClick={() => void query.refetch()}>
                {t('licensePlates.retry')}
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {query.isSuccess && (
        <Stack gap="md">
          <Group justify="space-between" wrap="wrap" gap="sm">
            <Group gap="sm" wrap="wrap">
              <SegmentedControl
                value={status}
                onChange={setStatus}
                data={[
                  { label: t('licensePlates.filterAny'), value: ANY_STATUS },
                  { label: t('licensePlates.status.open'), value: 'open' },
                  { label: t('licensePlates.status.closed'), value: 'closed' },
                  { label: t('licensePlates.status.disposed'), value: 'disposed' },
                ]}
              />
              <Text size="sm" c="dimmed">
                {t('licensePlates.total')} {total.toLocaleString()}
              </Text>
            </Group>
            <TextInput
              placeholder={t('licensePlates.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              leftSection={<IconSearch size={16} />}
              rightSection={
                search ? (
                  <CloseButton
                    size="sm"
                    onClick={() => setSearch('')}
                    aria-label={t('licensePlates.searchClear')}
                  />
                ) : null
              }
              w={{ base: '100%', sm: 260 }}
            />
          </Group>

          <Card withBorder radius="md" padding={0}>
            {rows.length === 0 ? (
              <Center py="xl">
                <Stack gap="xs" align="center">
                  <Text c="dimmed" size="sm">
                    {search.trim() || effectiveStatus
                      ? t('licensePlates.noResults')
                      : t('licensePlates.emptyBody')}
                  </Text>
                  {!search.trim() && !effectiveStatus && (
                    <Button
                      size="xs"
                      color="invetrak"
                      leftSection={<IconPlus size={14} />}
                      onClick={openCreate}
                    >
                      {t('licensePlates.new')}
                    </Button>
                  )}
                </Stack>
              </Center>
            ) : (
              <ResponsiveTable
                rows={rows}
                columns={columns}
                rowKey={(p) => p.licensePlateId}
                minWidth={760}
                actionsWidth={90}
                highlightOnHover
                verticalSpacing="sm"
                horizontalSpacing="md"
                actions={(p) => (
                  <>
                    <ActionIcon
                      variant="subtle"
                      color="invetrak"
                      aria-label={t('licensePlates.view')}
                      onClick={() => setViewing(p)}
                    >
                      <IconEye size={16} />
                    </ActionIcon>
                    <Menu position="bottom-end" withinPortal>
                      <Menu.Target>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={t('licensePlates.moreActions', {
                            number: p.licensePlateNumber,
                          })}
                        >
                          <IconDotsVertical size={16} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {/* A disposed plate gets a reason, not a row of dead controls. Disposal is
                            terminal — the server refuses to reopen one — and a greyed-out "Reopen"
                            would leave the operator clicking at something that can never work
                            without saying why. */}
                        {p.status === 'disposed' ? (
                          <Menu.Label>{t('licensePlates.disposedIsFinal')}</Menu.Label>
                        ) : (
                          <>
                            {canMove && p.itemCount > 0 && (
                              <>
                                <Menu.Item
                                  leftSection={<IconForklift size={14} />}
                                  onClick={() => setMoving({ plate: p, action: 'move' })}
                                >
                                  {t('licensePlates.moveAction')}
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={<IconPackageExport size={14} />}
                                  onClick={() => setMoving({ plate: p, action: 'unload' })}
                                >
                                  {t('licensePlates.unloadAction')}
                                </Menu.Item>
                                <Menu.Divider />
                              </>
                            )}
                            {p.status === 'closed' && (
                              <Menu.Item
                                onClick={() => retire.mutate({ id: p.licensePlateId, next: 'open' })}
                              >
                                {t('licensePlates.reopen')}
                              </Menu.Item>
                            )}
                            {p.status === 'open' && (
                              <Menu.Item
                                leftSection={<IconWashDrycleanOff size={14} />}
                                onClick={() =>
                                  retire.mutate({ id: p.licensePlateId, next: 'closed' })
                                }
                              >
                                {t('licensePlates.retire')}
                              </Menu.Item>
                            )}
                            <Menu.Item
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              onClick={() =>
                                retire.mutate({ id: p.licensePlateId, next: 'disposed' })
                              }
                            >
                              {t('licensePlates.dispose')}
                            </Menu.Item>
                          </>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </>
                )}
              />
            )}
          </Card>

          {pageCount > 1 && (
            <Group justify="center">
              <Pagination value={page} onChange={setPage} total={pageCount} color="invetrak" />
            </Group>
          )}
        </Stack>
      )}

      <CreateLicensePlatesModal opened={createOpen} onClose={closeCreate} onCreated={onCreated} />
      <LicensePlateDetailModal plate={viewing} onClose={() => setViewing(null)} />
      <MovePlateModal
        plate={moving?.plate ?? null}
        action={moving?.action ?? 'move'}
        onClose={() => setMoving(null)}
        onDone={onCreated}
      />
    </>
  );
}
